import { useState } from "react";

import {
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  Grid,
  Typography,
  Container,
  List,
  ListItem,
  ListSubheader,
  CardActions,
  Divider,
  Tooltip,
  Popover,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Loader } from "components/ui/";
import Properties from "./Properties";
import CopyLinkButton from "../CopyLinkButton";
import { useApi } from "hooks/api";
import { useQuery } from "react-query";
import { useMediaModal } from "hooks/mediaModal";
import Transition from "components/Transition";
import { getPublishUrl } from "utilities/mediaUrl";
import { DeploymentSite } from "types/qbank/deploymentSite";
import { useFilter } from "hooks/filter";
import CardImage from "../CardImage";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import Check from "@mui/icons-material/Check";
import { DeployedFile } from "types/qbank/deployedFile";
import ChildMedia from "./ChildMedia";
import { Media } from "types/qbank/media";

type IProps = {
  deploymentSite: DeploymentSite;
  onDoubleClick: (media: any, event: React.MouseEvent<HTMLDivElement>) => void;
};

export default function DetailDialog({ deploymentSite, onDoubleClick }: IProps) {
  const { modal, setModal } = useMediaModal();
  const { searchState } = useFilter();
  const { getMedia, getUser } = useApi();
  //const [parentMedia, setParentMedia] = useState<undefined | Media>(undefined);

  // @ts-ignore
  const media = useQuery(["getMedia", modal.media.mediaId], () =>
    // @ts-ignore
    getMedia(modal.media.mediaId)
  );

  const parentMedia = useQuery(["getMedia", media?.data?.parentId], () =>
    // @ts-ignore
    getMedia(media?.data?.parentId),
    {
      enabled: media?.data?.parentId !== media?.data?.mediaId,
      staleTime: Infinity,
    }
  );
  const showParentMedia = (parentId: number, mediaId: number) => {
    return parentId && parentId !== mediaId ? true : false;
  }

  const handleDoubleClick = (media: Media, event: React.MouseEvent<HTMLDivElement>) => {
    onDoubleClick(media, event);
  };

  const createdBy = media.data?.createdBy;
  const updatedBy = media.data?.updatedBy;
  const createdUser = useQuery(
    ["getUser", createdBy],
    // @ts-ignore
    () => getUser(createdBy),
    {
      enabled: !!createdBy,
      staleTime: Infinity,
    }
  );
  const updatedUser = useQuery(
    ["getUser", updatedBy],
    // @ts-ignore
    () => getUser(updatedBy),
    {
      enabled: !!updatedBy,
      staleTime: Infinity,
    }
  );

  const [templateId, setTemplateId] = useState<number>(
    parseInt(searchState.currentCategory.imageTemplateId)
  );
  const publishUrl = getPublishUrl(deploymentSite, media.data?.deployedFiles, templateId);
  const availableTemplates = media.data?.deployedFiles?.filter(
    (deployedFile) =>
      deployedFile.deploymentSiteId === deploymentSite.id
  );

  const getDeployedFileName = (deployedFile?: DeployedFile) => {
    try {
      if (!deployedFile)
        return '';

      var deployedFileMetadata = JSON.parse(deployedFile.metadata);
      if (deployedFileMetadata.templateName) {
        return deployedFileMetadata.templateName;
      }
      if (deployedFile.templateName) {
        return deployedFile.templateName;
      }

      if (deployedFileMetadata.width && deployedFileMetadata.height) {
        return deployedFileMetadata.width + " x " + deployedFileMetadata.height;
      }

      return deployedFile.filesize;
    } catch (error: any) {
      console.log(error.message);
      return !deployedFile ? '' : deployedFile.metadata;
    }
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  if (
    media.isLoading ||
    media.isError ||
    !media.data ||
    createdUser.isLoading ||
    createdUser.isError ||
    !createdUser ||
    updatedUser.isLoading ||
    updatedUser.isError ||
    !updatedUser
  ) {
    return <Loader />;
  }

  return (
    <Dialog
      fullScreen
      open={modal.open}
      onClose={() => setModal({ open: false })}
      TransitionComponent={Transition}
    >
      <AppBar elevation={0} position="fixed" color="default">
        <Toolbar variant="dense" sx={{ pl: 2, pr: 2 }} disableGutters>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setModal({ open: false })}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 8 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} sx={{ width: "100%" }}>
            <CardImage
              media={media.data}
              thumbnailSrc={publishUrl || ""}
              publishSrc={publishUrl || ""}
              alt={media.data.filename}
            />
            <CardActions disableSpacing>
              <Grid
                container
                justifyContent="space-between"
                sx={{
                  p: 1,
                }}
              >
                <Grid container item direction="column" xs="auto">
                  <Typography
                    variant="caption"
                    color="text.primary"
                    noWrap={true}
                  >
                    {media.data.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap={true}
                  >
                    {media.data.mediaId} | {media.data.mimetype.mimetype}
                  </Typography>
                </Grid>
                <Grid item zeroMinWidth>
                  {availableTemplates && availableTemplates.length > 0 && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.primary"
                        noWrap={true}
                        textOverflow="ellipsis"
                        sx={{ mr: 1 }}
                      >
                        {
                          getDeployedFileName(availableTemplates.find(
                            (template) =>
                              template.imageTemplateId === templateId
                              || (isNaN(templateId) && template.metadata.includes('original'))
                          ))
                        }
                      </Typography>
                      <Tooltip title="change template">
                        <IconButton
                          aria-label="change template"
                          onClick={handleClick}
                        >
                          <AspectRatioIcon />
                        </IconButton>
                      </Tooltip>
                      <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                      >
                        {availableTemplates.map((deployedFile, i) => {
                          const active =
                            templateId === deployedFile.imageTemplateId;
                          return (
                            <MenuItem
                              dense
                              divider
                              key={i}
                              value={deployedFile.imageTemplateId || 1}
                              onClick={() => {
                                setTemplateId(
                                  deployedFile.imageTemplateId || NaN
                                );
                                handleClose();
                              }}
                            >
                              {active && (
                                <ListItemIcon>
                                  <Check />
                                </ListItemIcon>
                              )}
                              <ListItemText inset={active ? false : true}>
                                {getDeployedFileName(deployedFile)}
                              </ListItemText>
                            </MenuItem>
                          );
                        })}
                      </Popover>
                    </>
                  )}
                  <CopyLinkButton
                    media={media.data}
                    disabled={false}
                    text={publishUrl || ""}
                    imageState={1}
                  />
                </Grid>
              </Grid>
            </CardActions>
          </Grid>

          <Grid item xs={12} md={6}>
            <List
              sx={{ width: "100%", bgcolor: "background.paper" }}
              component="nav"
              dense
              disablePadding
            >
              <ListItem dense>
                <Typography variant="subtitle1">
                  QBank ID:{" "}
                  <Typography variant="caption">
                    {media.data.mediaId}
                  </Typography>
                </Typography>
              </ListItem>
              <ListItem dense>
                <Typography variant="subtitle1">
                  Created:{" "}
                  <Typography variant="caption">
                    {new Date(media.data?.created).toISOString().split("T")[0]}
                  </Typography>
                </Typography>
              </ListItem>
              <ListItem dense>
                <Typography variant="subtitle1">
                  Created by:{" "}
                  <Typography variant="caption">
                    {createdUser.data?.name}
                  </Typography>
                </Typography>
              </ListItem>

              {updatedUser.data && media.data.updated && (
                <>
                  <ListItem dense>
                    <Typography variant="subtitle1">
                      Updated:{" "}
                      <Typography variant="caption">
                        {
                          new Date(media.data.updated)
                            .toISOString()
                            .split("T")[0]
                        }
                      </Typography>
                    </Typography>
                  </ListItem>
                  <ListItem dense>
                    <Typography variant="subtitle1">
                      Updated by:{" "}
                      <Typography variant="caption">
                        {updatedUser.data.name}
                      </Typography>
                    </Typography>
                  </ListItem>
                </>
              )}
            </List>

            {media.data.childMedias?.length > 0 &&
              (<>
                <Divider variant="middle" sx={{ m: 2 }} />
                <div className="group-media-header">Grouped by</div>
                <div className="group-media-main">
                  {media.data.childMedias.map((media) => {
                    return <ChildMedia media={media} deploymentSite={deploymentSite} onDoubleClick={handleDoubleClick}></ChildMedia>
                  })}
                </div>
              </>
              )
            }

            {parentMedia.data && showParentMedia(media.data.parentId, media.data.mediaId) &&
              <>
                <Divider variant="middle" sx={{ m: 2 }} />
                <div className="group-media-header">
                  This media is part of a group, the parent is displayed below
                </div>

                <div className="group-media-main">
                  <ChildMedia media={parentMedia.data} deploymentSite={deploymentSite} onDoubleClick={handleDoubleClick}></ChildMedia>
                </div>
              </>
            }

            <Divider variant="middle" sx={{ m: 2 }} />
            <List
              sx={{ width: "100%", bgcolor: "background.paper" }}
              component="nav"
              dense
              aria-labelledby="nested-list-subheader"
              disablePadding
              subheader={
                <ListSubheader component="div" id="nested-list-subheader">
                  Properties
                </ListSubheader>
              }
            >
              {media.data?.propertySets.map((item) => (
                <Properties key={item.id} propertySet={item} />
              ))}
            </List>
          </Grid>
        </Grid>
      </Container>
    </Dialog>
  );
}
